import { createTheme, CssBaseline, ThemeProvider } from "@mui/material"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React, { Suspense, useEffect, useMemo } from "react"
import { Helmet } from "react-helmet"
import { useDispatch, useSelector } from "react-redux"
import { RouterProvider } from "react-router-dom"
import AppRoutes from "./App.routes"
import "./App.scss"
import Loader from "./components/common/Loader/loader"
import logger from "./Logger"
import { UiActions } from "./store/ui-slice"
import { DARK_THEME, LIGHT_THEME, withBrandColor } from "./themes/theme-mui"
import { PRINTING_TIMEOUT } from "./utilities/constants"
import { asNumber, getReplaneConfig } from "./utils/replaneClient"

const pageLogger = logger.child({ module: "APP" })
const AppRouter = AppRoutes()

const queryClient = new QueryClient()

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import("@tanstack/react-query-devtools/production").then((d) => ({
    default: d.ReactQueryDevtools,
  }))
)

function App() {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.ui.showLoader)
  const printable = useSelector((state) => state.ui.showPrintable)
  const isDarkMode = useSelector((state) => state.ui.isDarkMode)
  const brandColor = useSelector((state) => state.ui.brandColor)
  const showDevTools = useSelector((state) => state.ui.showDevTools)

  // Apply the org white-label accent at the top level so CssBaseline, the global Loader, and every
  // route share the branded palette. brandColor is populated by AppLayout once org data loads
  // (it lives below Auth0/react-query, which App.js's body can't reach); until then this is the
  // base theme. withBrandColor returns the base theme unchanged when brandColor is unset/default.
  const appTheme = useMemo(
    () => createTheme(withBrandColor(isDarkMode ? DARK_THEME : LIGHT_THEME, brandColor)),
    [isDarkMode, brandColor]
  )

  useEffect(() => {
    // Apply the initial theme class (light by default, or the user's persisted choice).
    document.querySelector("body").classList.add(isDarkMode ? "theme-dark" : "theme-light")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(UiActions.populateTimeZones())
  }, [dispatch])

  useEffect(() => {
    if (!printable) return

    const handleAfterPrint = () => {
      pageLogger.info("After Printing Preview Window Close ...")
      // Print preview forces light; restore the user's preferred theme afterwards
      dispatch(UiActions.restoreUserTheme())
      dispatch(UiActions.setShowPrintable(false))
    }

    const delay = asNumber(getReplaneConfig("frontend", "ui.printing_timeout_ms", PRINTING_TIMEOUT), PRINTING_TIMEOUT)
    const timerId = setTimeout(() => {
      window.addEventListener("afterprint", handleAfterPrint)
      window.onafterprint = handleAfterPrint
    }, delay)

    return () => {
      clearTimeout(timerId)
      window.removeEventListener("afterprint", handleAfterPrint)
      window.onafterprint = null
    }
  }, [dispatch, printable])

  return (
    <div
      className={`App ct-app container-fluid ${isDarkMode ? "theme-dark" : "theme-light"} ${loading ? "page-loading" : ""} ${
        printable ? "print-preview" : "web-preview"
      }`}
      // style={
      //   printable
      //     ? {
      //         maxWidth: `${795 * window.devicePixelRatio}px`,
      //       }
      //     : {}
      // }
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={appTheme}>
          <Helmet>
            <title>ChaosTrack</title>
          </Helmet>
          <CssBaseline />
          <Loader />
          <Suspense fallback={<Loader enabled={true} />}>
            <RouterProvider router={AppRouter} />
          </Suspense>
        </ThemeProvider>
        {/* {showDevTools ? <ReactQueryDevtools initialIsOpen={true} buttonPosition="bottom-left" /> : <></>} */}
        {showDevTools ? (
          <Suspense fallback={null}>
            <ReactQueryDevtoolsProduction initialIsOpen={true} buttonPosition="bottom-left" />
          </Suspense>
        ) : (
          <></>
        )}
      </QueryClientProvider>
      {/* <Container /> */}
    </div>
  )
}

export default App
